'use client'

import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'

import { cn } from '../../utils'
import { Interactive } from '@highlight/ui'

export const Popover = PopoverPrimitive.Root

export const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <PopoverPrimitive.Trigger ref={ref} data-slot="popover" className={cn('', className)} {...props}>
    {children}
  </PopoverPrimitive.Trigger>
))
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName

export const PopoverClose = PopoverPrimitive.Close

export const PopoverAnchor = PopoverPrimitive.Anchor

interface PopoverPortalProps extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Portal> {
  disablePortal?: boolean
}

const PopoverPortal = React.forwardRef<React.ElementRef<typeof PopoverPrimitive.Portal>, PopoverPortalProps>(
  ({ disablePortal, children, ...props }, ref) => {
    if (disablePortal) {
      return <>{children}</>
    }

    return <PopoverPrimitive.Portal {...props}>{children}</PopoverPrimitive.Portal>
  },
)
PopoverPortal.displayName = PopoverPrimitive.Portal.displayName

interface PopoverContentProps extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  disablePortal?: boolean
}

export const PopoverContent = React.forwardRef<React.ElementRef<typeof PopoverPrimitive.Content>, PopoverContentProps>(
  ({ className, align = 'center', sideOffset = 4, disablePortal, ...props }, ref) => (
    <PopoverPortal disablePortal={disablePortal}>
      <Interactive>
        <PopoverPrimitive.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            'bg-background-overlay text-foreground pointer-events-auto z-50 w-72 rounded-2xl p-4 shadow-xl',

            // Like @highlight/ui Popover and Menu: lands at once, since opening answers a click and any
            // entrance reads as lag, and fades out so it doesn't blink away.
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-150',
            // Hidden until Radix has positioned it (data-side), so it never flashes at the origin first.
            'opacity-0 data-[state=open]:data-[side]:opacity-100',
            // The popup holds focus for its contents; a ring around the whole box reads as a selected box.
            'focus-visible:outline-none',

            className,
          )}
          {...props}
        />
      </Interactive>
    </PopoverPortal>
  ),
)
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export const PopoverArrow = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Arrow>
>(({ className, width = 20, height = 10, ...props }, ref) => {
  return (
    <PopoverPrimitive.Arrow
      ref={ref}
      className={cn('fill-[#191919]', className)}
      width={width}
      height={height}
      style={{
        // Use drop-shadow to create border effect that blends with container
        filter: 'drop-shadow(0 -1px 0 hsl(var(--light-5)))',
        // Clip the bottom part to hide the shadow that would overlap the content
        clipPath: 'inset(-10px -10px 0 -10px)',
      }}
      {...props}
    />
  )
})
PopoverArrow.displayName = PopoverPrimitive.Arrow.displayName
